import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Box } from '@mui/material';
import { AxiosError } from 'axios';
import { FC } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IJobBoardPayload, addJobBoardAccounts } from 'wisestep-api';
import { ControlledTextInput, WsButton } from 'wisestep-base';
import { object, string } from 'yup';

import { useSKUModalStore } from '../../../../components/skuModal/SKUModalStore';
import {
  useNativeSearchAccessControl,
  NativeSearchAction,
} from '../../../../hooks/useNativeSearchAccessControl';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
export interface NewAccountProps {
  accountType:
    | 'dice'
    | 'CBRDB'
    | 'TF'
    | 'CVL'
    | 'RL'
    | 'CBRE'
    | 'MONSTER'
    | 'CW_JOBS'
    | 'JOB_SITE'
    | 'JOBSERVE';
}
const defaultAttributes = {
  DICESEARCH: {
    jbName: 'DICE_SEARCH',
    url: '/native-search-settings',
  },
  CAREER_BUILDER: {
    jbName: 'CAREER_BUILDER',
    url: '/native-search-settings/cb-rdb',
  },
  TECH_FETCH: {
    jbName: 'TECH_FETCH',
    url: '/native-search-settings/techFetch',
  },
  CV_LIBRARY: {
    jbName: 'CV_LIBRARY',
    url: '/native-search-settings/cv-library',
  },
  MONSTER: {
    jbName: 'MONSTER',
    url: '/native-search-settings/monster',
  },
  JOBSERVE: {
    jbName: 'JOB_SERVE',
    url: '/native-search-settings/job-serve',
  },
  CW_JOBS: {
    jbName: 'CW_JOBS',
    url: '/native-search-settings/cw-job',
  },
  JOB_SITE: {
    jbName: 'JOB_SITE',
    url: '/native-search-settings/job-site',
  },
};
const NewAccount: FC<NewAccountProps> = ({ accountType }) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const navigate = useNavigate();
  const setModal = useSKUModalStore((state) => state.setModal);
  const { access, owns } = useNativeSearchAccessControl([
    NativeSearchAction.MANAGE_NATIVE_SEARCH_ACCOUNTS,
  ]);
  const [manage] = access;
  const validationSchema = object().shape({
    username: string().required('Username is required'),
    password: string()
      .min(4, 'Password must be at least 4 characters')
      .required('Password is required'),
  });
  const defaultData = {
    username: '',
    password: '',
  };
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: defaultData,
  };

  const methods = useForm(formOptions);
  const { handleSubmit } = methods;

  const apiCallHandler = async (jobBoardName: string, userName, password) => {
    const payload: IJobBoardPayload = {
      jobBoardName: jobBoardName,
      username: userName,
      password: password,
    };
    await addJobBoardAccounts(payload);
  };
  const onSaveHandler = (data: FieldValues) => {
    if (!owns && !manage) {
      setNotification(
        warning(
          'Insufficient Permissions,Please Contact workspace Admin for permission to add account'
        )
      );
      return;
    }
    const handler = {
      ['dice']: async () => {
        try {
          await apiCallHandler(
            defaultAttributes['DICESEARCH'].jbName,
            data.username,
            data.password
          );
          setNotification(success('Account Configured, Successfully.'));
          navigate(defaultAttributes['DICESEARCH'].url);
        } catch (error: any) {
          if (error.response.status == 402) {
            setModal({ object: error.response.data, type: 'count' });
          }
          setNotification(warning('Something went wrong, Please try again.'));
        }
      },
      ['CBRDB']: async () => {
        try {
          await apiCallHandler(
            defaultAttributes['CAREER_BUILDER'].jbName,
            data.username,
            data.password
          );
          setNotification(success('Account Configured, Successfully.'));
          navigate(defaultAttributes['CAREER_BUILDER'].url);
        } catch (error: any) {
          if (error.response.status == 402) {
            setModal({ object: error.response.data, type: 'count' });
          }
          setNotification(warning('Something went wrong, Please try again.'));
        }
      },
      ['TF']: async () => {
        try {
          await apiCallHandler(
            defaultAttributes['TECH_FETCH'].jbName,
            data.username,
            data.password
          );
          setNotification(success('Account Configured, Successfully.'));
          navigate(defaultAttributes['TECH_FETCH'].url);
        } catch (error: any) {
          if (error.response.status == 402) {
            setModal({ object: error.response.data, type: 'count' });
          }
          setNotification(warning('Something went wrong, Please try again.'));
        }
      },
      ['CVL']: async () => {
        try {
          await apiCallHandler(
            defaultAttributes['CV_LIBRARY'].jbName,
            data.username,
            data.password
          );
          setNotification(success('Account Configured, Successfully.'));
          navigate(defaultAttributes['CV_LIBRARY'].url);
        } catch (error: any) {
          if (error.response.status == 402) {
            setModal({ object: error.response.data, type: 'count' });
          }
          setNotification(warning('Something went wrong, Please try again.'));
        }
      },
      ['MONSTER']: async () => {
        try {
          await apiCallHandler(defaultAttributes['MONSTER'].jbName, data.username, data.password);
          setNotification(success('Account Configured, Successfully.'));
          navigate(defaultAttributes['MONSTER'].url);
        } catch (error: any) {
          if (error.response.status == 402) {
            setModal({ object: error.response.data, type: 'count' });
          }
          setNotification(warning('Something went wrong, Please try again.'));
        }
      },
      ['JOBSERVE']: async () => {
        try {
          await apiCallHandler(defaultAttributes['JOBSERVE'].jbName, data.username, data.password);
          setNotification(success('Account Configured, Successfully.'));
          navigate(defaultAttributes['JOBSERVE'].url);
        } catch (error) {
          setNotification(warning('Something went wrong, Please try again.'));
        }
      },
      ['CW_JOBS']: async () => {
        try {
          await apiCallHandler(defaultAttributes['CW_JOBS'].jbName, data.username, data.password);
          setNotification(success('Account Configured, Successfully.'));
          navigate(defaultAttributes['CW_JOBS'].url);
        } catch (error) {
          const err = error as AxiosError;
          if (err.response?.data.status) {
            setNotification(failed(err.response?.data.status));
          } else {
            setNotification(failed('Something went wrong, Please try again.'));
          }
        }
      },
      ['JOB_SITE']: async () => {
        try {
          await apiCallHandler(defaultAttributes['JOB_SITE'].jbName, data.username, data.password);
          setNotification(success('Account Configured, Successfully.'));
          navigate(defaultAttributes['JOB_SITE'].url);
        } catch (error) {
          setNotification(warning('Something went wrong, Please try again.'));
        }
      },
    };

    handler[accountType]();
  };
  return (
    <Box borderRadius={1.5}>
      <FormProvider {...methods}>
        <Grid item xs={10} pl={2} pt={2}>
          <ControlledTextInput
            name="username"
            labelText={accountType != 'MONSTER' ? 'Username*' : 'Account Name'}
            type="text"
            fullWidth
            id="username"
            placeholder={accountType != 'MONSTER' ? 'Enter username' : 'Enter Account Name'}
            variant="outlined"
            required
            autoComplete="username"
          />
        </Grid>
        <Grid item xs={10} pt={4} pl={2}>
          <ControlledTextInput
            name="password"
            labelText={accountType != 'MONSTER' ? 'Password*' : 'CAT'}
            type={accountType != 'MONSTER' ? 'password' : 'text'}
            fullWidth
            id="password"
            placeholder={accountType != 'MONSTER' ? 'Enter password' : 'Enter CAT'}
            variant="outlined"
            required
            autoComplete="password"
          />
        </Grid>

        <Grid item xs={12} pl={2} pt={5}>
          <WsButton
            variant="contained"
            size="large"
            type="submit"
            onClick={handleSubmit(onSaveHandler)}
          >
            Continue
          </WsButton>
        </Grid>
      </FormProvider>
    </Box>
  );
};

export default NewAccount;
